import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button, Heading, Text } from "@nbs/ui";

/** Same support inbox as the header licence banner. */
const SUPPORT_EMAIL = "azizmashkour@gmail.com";

type Props = {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
};

type State = {
  hasError: boolean;
  message: string;
};

function supportMailto(detail: string): string {
  const subject = "Support Écosystèmes Guinée — erreur applicative";
  const body = [
    "Bonjour,",
    "",
    "Une erreur s’est produite dans l’application Écosystèmes Guinée.",
    detail ? `Détail technique : ${detail}` : null,
    `Page : ${typeof window !== "undefined" ? window.location.href : "(inconnue)"}`,
    "",
    "Merci de m’aider à résoudre le problème.",
  ]
    .filter((line) => line != null)
    .join("\n");
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Full-viewport error fallback aligned with BareLayout / Forbidden pages:
 * chromeless, centered, brand mark, no raw left-aligned dump.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private reset = () => {
    this.setState({ hasError: false, message: "" });
  };

  override render() {
    if (this.state.hasError) {
      const title = this.props.fallbackTitle ?? "Une erreur est survenue";
      const body =
        this.props.fallbackMessage ??
        "Nous n’avons pas pu afficher cette page. Réessayez, ou contactez l’administrateur si le problème persiste.";
      const detail = this.state.message;

      return (
        <div className="nbs-bare nbs-error-boundary" role="alert">
          <a className="nbs-bare__brand" href="/" aria-label="Écosystèmes Guinée">
            <span className="nbs-bare__mark" aria-hidden="true">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3c3.5 2 6 5 6 9 0 3.3-2.7 6-6 6s-6-2.7-6-6c0-4 2.5-7 6-9Z"
                  fill="#bfe6cd"
                />
                <path
                  d="M12 21V9m0 4 3-2m-3 5-2.6-1.7"
                  stroke="#0f3d28"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            Écosystèmes Guinée
          </a>

          <section className="nbs-page-state nbs-error-boundary__panel">
            <span className="nbs-bare__glyph" aria-hidden="true">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 8v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="16.5" r="1.1" fill="currentColor" />
              </svg>
            </span>
            <Heading level={1}>{title}</Heading>
            <Text tone="muted">{body}</Text>
            <Text tone="muted" className="nbs-error-boundary__contact-hint">
              Si l’erreur revient, contactez l’administrateur (
              <a className="nbs-error-boundary__contact-link" href={supportMailto(detail)}>
                {SUPPORT_EMAIL}
              </a>
              ) en joignant le détail technique ci-dessous.
            </Text>
            {detail ? (
              <p className="nbs-error-boundary__detail">
                <span className="nbs-error-boundary__detail-label">Détail technique</span>
                {detail}
              </p>
            ) : null}
            <div className="nbs-login-page__actions">
              <Button variant="primary" onClick={this.reset}>
                Réessayer
              </Button>
              <a href={supportMailto(detail)}>
                <Button variant="secondary" type="button">
                  Contacter l’administrateur
                </Button>
              </a>
              <a href="/">
                <Button variant="ghost" type="button">
                  Accueil
                </Button>
              </a>
            </div>
          </section>
        </div>
      );
    }
    return this.props.children;
  }
}
